import { getReq, post } from '../service/API'
import useSWR from 'swr'
import { ENDPOINTS } from '../service/ENDPOINTS'
import { SubjectType } from '../Types'
import { mutate as mutateGlobal } from 'swr/_internal'
import { PAGINATION_LIMIT, wait } from '../constants'
import { useState } from 'react'
import { ENDPOINTS_V2 } from '../service/ENDPOINTS_V2'

export const useSubject = (projectId: string, subjectId: string) => {
  const path = ENDPOINTS.SUBJECTS.getSubject(projectId, subjectId)

  const { data, error, mutate } = useSWR(path, getReq, { suspense: true })

  const typedData = data.data as SubjectType

  return {
    data: !data ? data : typedData,
    isLoading: !error && !data,
    isError: error,
    mutate: mutate,
  }
}

/**
 *
 * COMPLETE SUBJECT
 *
 * For completing a subject in the subject journey
 *
 * @param projectId
 * @param subjectId
 * @param siteId
 */

export const useCompleteSubject = (projectId: string, subjectId: string, siteId: string) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const callComplete = (obj) => {
    setIsLoading(true)

    return post(ENDPOINTS.SUBJECTS.ACTION.complete(projectId), obj, 'PUT')
      .then(async () => {
        await wait(1000)
        setIsLoading(false)

        mutateGlobal(ENDPOINTS_V2.SUBJECTS.getSubjectAssignments(projectId, subjectId))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubject(projectId, subjectId))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubjectAuditPagination(projectId, subjectId, 1, PAGINATION_LIMIT))
        mutateGlobal(ENDPOINTS.SUBJECTS.get(projectId, siteId))
      })
      .catch((error) => {
        setIsLoading(false)
        alert(error.message)
      })
  }

  return {
    callComplete: callComplete,
    isLoading: isLoading,
  }
}

/**
 *
 * COMPLETE SUBJECT
 *
 * For completing a subject in the subject journey
 *
 * @param projectId
 * @param subjectId
 * @param siteId
 *
 * ******************************************************************************************/

export const useUnCompleteSubject = (projectId: string, subjectId: string) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const callUnComplete = (obj) => {
    setIsLoading(true)
    return post(ENDPOINTS.SUBJECTS.ACTION.uncomplete(projectId), obj, 'PUT')
      .then(async () => {
        await wait(1000)
        setIsLoading(false)

        mutateGlobal(ENDPOINTS_V2.SUBJECTS.getSubjectAssignments(projectId, subjectId))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubject(projectId, subjectId))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubjectAuditPagination(projectId, subjectId, 1, PAGINATION_LIMIT))
        //mutateGlobal(ENDPOINTS.SUBJECTS.get(projectId, siteId))
      })
      .catch((error) => {
        setIsLoading(false)
        alert(error.message)
      })
  }

  return {
    callUnComplete: callUnComplete,
    isLoading: isLoading,
  }
}

/**
 * MOVE FORWARD
 * For moving the subject forward in the subject journey
 *
 * @param projectId
 * @param subjectId
 *
 ******************************************************************************************/
export const useMoveSubjectForward = (projectId: string, subjectId: string) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // Commit: the original move flow. Run when the body part is valid, or when the user
  // confirms the override from the warning drawer.
  const callMoveForward = (obj) => {
    setIsLoading(true)
    return post(ENDPOINTS.SUBJECTS.ACTION.moveEligibility(projectId), obj, 'PUT')
      .then(async () => {
        await wait(1000)
        setIsLoading(false)

        mutateGlobal(ENDPOINTS_V2.SUBJECTS.getSubjectAssignments(projectId, subjectId))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubject(projectId, subjectId))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubjectAuditPagination(projectId, subjectId, 1, PAGINATION_LIMIT))
        mutateGlobal(ENDPOINTS.SUBJECTS.useGroups(projectId, subjectId))
        mutateGlobal(ENDPOINTS.SUBJECTS.eligibilityActions(projectId, subjectId))
      })
      .catch((error) => {
        setIsLoading(false)
        alert(error.message)
      })
  }

  return {
    callMoveForward: callMoveForward,
    isLoading: isLoading,
  }
}

/**
 * MOVE FORWARD NO ELIGIBILITY
 * For moving the subject forward in the subject journey
 *
 * @param projectId
 * @param subjectId
 *
 ******************************************************************************************/
export const useMoveForwardNoEligibility = (projectId: string, subjectId: string) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const callMoveForwardNoEligibility = (obj) => {
    console.log('useSubject > yesy yes = ', obj)
    setIsLoading(true)
    return post(ENDPOINTS.SUBJECTS.ACTION.move(projectId), obj, 'PUT')
      .then(async (result) => {
        console.log('useSubject > result = ', result)
        await wait(1000)
        setIsLoading(false)

        mutateGlobal(ENDPOINTS_V2.SUBJECTS.getSubjectAssignments(projectId, subjectId))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubject(projectId, subjectId))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubjectAuditPagination(projectId, subjectId, 1, PAGINATION_LIMIT))
        mutateGlobal(ENDPOINTS.SUBJECTS.useGroups(projectId, subjectId))
        return result
      })
      .catch((error) => {
        setIsLoading(false)
        alert(error.message)
        return error
      })
  }

  return {
    callMoveForwardNoEligibility: callMoveForwardNoEligibility,
    isLoading: isLoading,
  }
}

/**
 * MOVE BACK
 * For moving the subject back in the subject journey
 *
 * @param projectId
 * @param subjectId
 *
 ******************************************************************************************/
export const useMoveSubjectBack = (projectId: string, subjectId: string) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const callMoveBack = (obj) => {
    setIsLoading(true)
    return post(ENDPOINTS.SUBJECTS.ACTION.movePrevious(projectId), obj, 'PUT')
      .then(async () => {
        await wait(1000)
        setIsLoading(false)

        mutateGlobal(ENDPOINTS_V2.SUBJECTS.getSubjectAssignments(projectId, subjectId))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubject(projectId, subjectId))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubjectAuditPagination(projectId, subjectId, 1, PAGINATION_LIMIT))
        mutateGlobal(ENDPOINTS.SUBJECTS.useGroups(projectId, subjectId))
      })
      .catch((error) => {
        setIsLoading(false)
        alert(error.message)
      })
  }

  return {
    callMoveBack: callMoveBack,
    isLoading: isLoading,
  }
}

/**
 * TERMINATE SUBJECT
 * For terminating the subject in the subject journey
 *
 * @param projectId
 * @param subjectId
 *
 ******************************************************************************************/
export const useTerminateSubject = (projectId: string, subjectId: string, siteId: string) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const callTerminate = (obj) => {
    setIsLoading(true)

    return post(ENDPOINTS.SUBJECTS.ACTION.disqualify(projectId), obj, 'PUT')
      .then(async () => {
        await wait(1000)
        setIsLoading(false)

        mutateGlobal(ENDPOINTS.SUBJECTS.get(projectId, siteId))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubjectAuditPagination(projectId, subjectId, 1, PAGINATION_LIMIT))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubject(projectId, subjectId))
        mutateGlobal(ENDPOINTS.SUBJECTS.eligibilityActions(projectId, subjectId))
      })
      .catch((error) => {
        setIsLoading(false)
        alert(error.message)
      })
  }

  return {
    callTerminate: callTerminate,
    isLoading: isLoading,
  }
}

/**
 * UN-TERMINATE/REACTIVATE SUBJECT
 * For un-terminating/reactivating the subject in the subject journey
 *
 * @param projectId
 * @param subjectId
 *
 ******************************************************************************************/
export const useReactivateSubject = (projectId: string, subjectId: string, siteId: string) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const callReactivate = (obj) => {
    setIsLoading(true)

    return post(ENDPOINTS.SUBJECTS.ACTION.requalify(projectId), obj, 'PUT')
      .then(async () => {
        await wait(1000)
        setIsLoading(false)

        mutateGlobal(ENDPOINTS.SUBJECTS.get(projectId, siteId))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubject(projectId, subjectId))
        mutateGlobal(ENDPOINTS.SUBJECTS.getSubjectAuditPagination(projectId, subjectId, 1, PAGINATION_LIMIT))
      })
      .catch((error) => {
        setIsLoading(false)
        alert(error.message)
      })
  }

  return {
    callReactivate: callReactivate,
    isLoading: isLoading,
  }
}
